import { useEffect, useMemo, useState } from 'react';
import { IonSpinner } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useTagsRegistry } from '../../hooks/useTagsRegistry';
import { useUpdateDietaryPreferences } from '../../hooks/useProfile';
import { useProfileStore } from '../../store/useProfileStore';
import { hasAnsweredDietaryPrompt } from '../../types/profile';
import { SelectableChip } from '../ui/SelectableChip';

interface DietaryPreferencesEditorProps {
  /** Fired after a successful save — the modal host uses it to close itself. */
  onSaved?: () => void;
}

function toggleInList(list: string[], item: string): string[] {
  return list.includes(item) ? list.filter((v) => v !== item) : [...list, item];
}

/**
 * The one dietary-restrictions editor. Rendered inline as a section on
 * ProfilePage and as the body of DietaryPreferencesModal (opened from the
 * home-feed prompt card), so both surfaces are the same UI by construction.
 *
 * Chip universe comes from the BE tags registry (`useTagsRegistry`) — the same
 * `dietary` and `allergens` sets a chef tags a dish with and the feed filter
 * sheet offers. Nothing about the option list is hardcoded here.
 *
 * Saving always sends BOTH arrays, empty included: an empty pair is the "I have
 * no restrictions" answer, which the BE records by flipping `dietaryPrefsSet`.
 * That's what retires the prompt card, so the Save button stays enabled with
 * nothing selected as long as the user hasn't answered yet.
 */
export const DietaryPreferencesEditor: React.FC<DietaryPreferencesEditorProps> = ({ onSaved }) => {
  const { t } = useTranslation('profile');
  const { t: tCommon } = useTranslation('common');
  // Property access rather than destructuring: TanStack's status union narrows a
  // destructured `data` to `never` when paired with a status flag.
  const tagsQuery = useTagsRegistry();
  const tags = tagsQuery.data;
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const save = useUpdateDietaryPreferences();

  const savedDietary = useMemo(() => activeProfile?.dietaryTags ?? [], [activeProfile?.dietaryTags]);
  const savedAllergens = useMemo(
    () => activeProfile?.allergenTags ?? [],
    [activeProfile?.allergenTags],
  );

  const [dietary, setDietary] = useState<string[]>(savedDietary);
  const [allergens, setAllergens] = useState<string[]>(savedAllergens);
  // Once the user touches a chip the draft is theirs — the re-seed effect below
  // stops overwriting it even if a background profile refetch lands.
  const [touched, setTouched] = useState(false);

  // Re-seed from the profile while the draft is still pristine. The editor can
  // mount before whoami/useActiveProfile populates the store (home-feed card on
  // a cold boot), in which case the initial state above was seeded from an
  // empty profile and would otherwise show every chip unselected.
  const savedKey = `${savedDietary.join('|')}::${savedAllergens.join('|')}`;
  useEffect(() => {
    if (touched) return;
    setDietary(savedDietary);
    setAllergens(savedAllergens);
    // `savedKey` collapses both arrays into one primitive dep so the effect
    // doesn't refire on every render from fresh array identities.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey, touched]);

  const answered = hasAnsweredDietaryPrompt(activeProfile);
  const empty = dietary.length === 0 && allergens.length === 0;
  const dirty =
    dietary.join('|') !== savedDietary.join('|') || allergens.join('|') !== savedAllergens.join('|');

  const handleSave = () => {
    if (!activeProfile) return;
    save.mutate(
      { profileId: activeProfile.id, dietaryTags: dietary, allergenTags: allergens },
      {
        onSuccess: () => {
          setTouched(false);
          onSaved?.();
        },
      },
    );
  };

  // Spin only while the registry is actually in flight. On a failed fetch the
  // hook's placeholder leaves both categories empty and we fall through to the
  // empty chip groups — same degradation as the feed filter sheet, rather than
  // a spinner that never resolves.
  if (tagsQuery.isFetching && !tags?.dietary.length && !tags?.allergens.length) {
    return (
      <div className="flex justify-center py-6">
        <IonSpinner name="crescent" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase text-muted-foreground">
          {t('dietary.dietaryLabel')}
        </p>
        <div className="flex flex-wrap gap-2">
          {tags?.dietary.map((tag) => (
            <SelectableChip
              key={tag.value}
              active={dietary.includes(tag.value)}
              disabled={save.isPending}
              onClick={() => {
                setTouched(true);
                setDietary((prev) => toggleInList(prev, tag.value));
              }}
            >
              {tag.label}
            </SelectableChip>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase text-muted-foreground">
          {t('dietary.allergenLabel')}
        </p>
        <div className="flex flex-wrap gap-2">
          {tags?.allergens.map((tag) => (
            <SelectableChip
              key={tag.value}
              active={allergens.includes(tag.value)}
              disabled={save.isPending}
              onClick={() => {
                setTouched(true);
                setAllergens((prev) => toggleInList(prev, tag.value));
              }}
            >
              {tag.label}
            </SelectableChip>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        {/* Disabled only once there's nothing left to record: the user has
            already answered AND hasn't changed anything since. A first-time
            user with nothing selected still gets a live button — that click IS
            the "no restrictions" answer. */}
        <button
          type="button"
          onClick={handleSave}
          disabled={save.isPending || !activeProfile || (answered && !dirty)}
          className="rounded-full bg-[var(--ion-color-primary)] px-5 py-2 text-sm font-semibold text-white shadow-sm active:scale-[0.99] transition-transform disabled:opacity-50"
        >
          {empty && !answered ? t('dietary.saveNone') : tCommon('actions.save')}
        </button>
      </div>
    </div>
  );
};
